import styled from "styled-components";
import { useRef, useState } from "react";

import { theme } from "./theme";
import { useT } from "./i18n";

// Country dial code + national number, matching the send-voucher WhatsApp modal. Reused by the drawer
// phone edit and the Buat Booking Baru guest form. Controlled: `value` is the composed digits (e.g.
// "6281234567890"); `onChange` reports the composed digits back.
export const WA_COUNTRIES = [
  { code: "62", flag: "🇮🇩", name: "Indonesia" },
  { code: "60", flag: "🇲🇾", name: "Malaysia" },
  { code: "65", flag: "🇸🇬", name: "Singapore" },
  { code: "66", flag: "🇹🇭", name: "Thailand" },
  { code: "63", flag: "🇵🇭", name: "Philippines" },
  { code: "84", flag: "🇻🇳", name: "Vietnam" },
  { code: "855", flag: "🇰🇭", name: "Cambodia" },
  { code: "856", flag: "🇱🇦", name: "Laos" },
  { code: "95", flag: "🇲🇲", name: "Myanmar" },
  { code: "61", flag: "🇦🇺", name: "Australia" },
  { code: "64", flag: "🇳🇿", name: "New Zealand" },
  { code: "81", flag: "🇯🇵", name: "Japan" },
  { code: "82", flag: "🇰🇷", name: "South Korea" },
  { code: "86", flag: "🇨🇳", name: "China" },
  { code: "852", flag: "🇭🇰", name: "Hong Kong" },
  { code: "886", flag: "🇹🇼", name: "Taiwan" },
  { code: "91", flag: "🇮🇳", name: "India" },
  { code: "94", flag: "🇱🇰", name: "Sri Lanka" },
  { code: "44", flag: "🇬🇧", name: "United Kingdom" },
  { code: "49", flag: "🇩🇪", name: "Germany" },
  { code: "33", flag: "🇫🇷", name: "France" },
  { code: "31", flag: "🇳🇱", name: "Netherlands" },
  { code: "34", flag: "🇪🇸", name: "Spain" },
  { code: "39", flag: "🇮🇹", name: "Italy" },
  { code: "1", flag: "🇺🇸", name: "USA / Canada" },
];

// Checked longest-first so multi-digit codes (855/856/852/886) win before shorter ones.
const WA_KNOWN_CODES = [
  "855",
  "856",
  "852",
  "886",
  "62",
  "60",
  "65",
  "66",
  "63",
  "84",
  "95",
  "61",
  "64",
  "81",
  "82",
  "86",
  "91",
  "94",
  "44",
  "49",
  "33",
  "31",
  "34",
  "39",
  "1",
];

export const detectWaCountry = (raw) => {
  const digits = (raw || "").replace(/\D/g, "");
  const match = WA_KNOWN_CODES.find((code) => digits.startsWith(code));
  return match
    ? { code: match, local: digits.slice(match.length) }
    : { code: "62", local: digits };
};

const Row = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const Select = styled.select`
  flex: 0 0 auto;
  max-width: 170px;
  padding: 0.6rem 0.5rem;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  font-size: 1rem;
  background: #ffffff;
`;

const Input = styled.input`
  flex: 1 1 auto;
  min-width: 0;
  padding: 0.6rem 0.7rem;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  font-size: 1rem;
`;

const PhoneField = (props) => {
  const { t } = useT();
  const detected = detectWaCountry(props.value || "");
  const knownInitial = WA_COUNTRIES.some(
    (country) => country.code === detected.code,
  );
  const [country, setCountry] = useState(
    knownInitial ? detected.code : "other",
  );
  const [manualCode, setManualCode] = useState(
    knownInitial ? "" : detected.code,
  );
  const [local, setLocal] = useState(detected.local);
  const composedRef = useRef(props.value || "");
  const [prevValue, setPrevValue] = useState(props.value || "");

  // Re-seed the breakdown when the parent replaces the value externally (reset / different guest) — the
  // "derive during render" pattern (guarded, no effect, no loop). Skipped when the change came from our
  // own onChange (composedRef), so typing never re-parses mid-entry.
  if ((props.value || "") !== prevValue) {
    setPrevValue(props.value || "");
    if ((props.value || "") !== composedRef.current) {
      const next = detectWaCountry(props.value || "");
      const known = WA_COUNTRIES.some((entry) => entry.code === next.code);
      setCountry(known ? next.code : "other");
      setManualCode(known ? "" : next.code);
      setLocal(next.local);
      composedRef.current = props.value || "";
    }
  }

  const emit = (nextCountry, nextManual, nextLocal) => {
    const code = (nextCountry === "other" ? nextManual : nextCountry).replace(
      /\D/g,
      "",
    );
    const national = nextLocal.replace(/\D/g, "").replace(/^0+/, "");
    const composed = `${code}${national}`;
    composedRef.current = composed;
    props.onChange(composed);
  };

  return (
    <>
      <Row>
        <Select
          value={country}
          onChange={(event) => {
            setCountry(event.target.value);
            emit(event.target.value, manualCode, local);
          }}
        >
          {WA_COUNTRIES.map((entry) => (
            <option key={entry.code} value={entry.code}>
              {entry.flag} +{entry.code} {entry.name}
            </option>
          ))}
          <option value="other">🌐 {t("wa_other")}</option>
        </Select>
        <Input
          type="text"
          inputMode="numeric"
          autoFocus={props.autoFocus}
          placeholder="81234567890"
          value={local}
          onChange={(event) => {
            setLocal(event.target.value);
            emit(country, manualCode, event.target.value);
          }}
        />
      </Row>
      {country === "other" ? (
        <Input
          style={{ marginTop: "0.5rem", width: "100%" }}
          type="text"
          inputMode="numeric"
          placeholder={t("wa_code_placeholder")}
          value={manualCode}
          onChange={(event) => {
            setManualCode(event.target.value);
            emit(country, event.target.value, local);
          }}
        />
      ) : null}
    </>
  );
};

export default PhoneField;
